import * as turf from "@turf/turf"
import { feature_from_id } from "../utils/layer_actions"
import system_constants from "../configs/system.json"
import feature_schema from "../configs/feature_schema.json"

const produce_id = (geo_json) => {
  const ids = geo_json.features.map((feature) => feature.id)
  let max_id = Math.max(...ids)
  max_id =
    max_id < system_constants.temp_id_range
      ? system_constants.temp_id_range
      : max_id + 1
  return max_id
}

const create_point = (id, latlng, properties = {}) => {
  const { lng, lat } = latlng
  return {
    type: "Feature",
    id,
    geometry: {
      type: "Point",
      coordinates: [lng, lat],
    },
    properties: {
      ...properties,
    },
  }
}

const categorise_allocation = (allocation_array) => {
  if (Array.isArray(allocation_array)) {
    if (allocation_array.length > 1) {
      return -2
    } else {
      return parseInt(allocation_array[0])
    }
  } else {
    return -1
  }
}

const adjust_cup_boundary = (cup_id, au_geo_json) => {
  if (cup_id === null || cup_id === undefined) {
    return []
  }
  const aus_assigned_to_cup = au_geo_json.features.filter(
    (au) => parseInt(au.properties.cup_id) === cup_id,
  )
  if (aus_assigned_to_cup.length === 0) {
    return []
  }
  const aus_dissolved = turf.dissolve(
    turf.featureCollection(aus_assigned_to_cup),
  )
  aus_dissolved.features.forEach((feature) => {
    feature.properties["cup_id"] = cup_id
    feature["id"] = parseInt(cup_id)
  })
  return aus_dissolved.features
}

const update_cup_boundaries = (
  affected_cup_ids,
  au_geo_json,
  cup_boundary_geo_json,
) => {
  const affectedSet = new Set(affected_cup_ids.map(Number))
  const unaffected_features = cup_boundary_geo_json.features.filter(
    (f) => !affectedSet.has(Number(f.properties.cup_id)),
  )
  const new_cups = affected_cup_ids.map((cup_id) =>
    adjust_cup_boundary(cup_id, au_geo_json),
  )
  const updated_cups = {
    type: "FeatureCollection",
    features: [...unaffected_features, ...new_cups.flat()],
  }
  return updated_cups
}

export const initialMapDataState = {
  fac_geo_json: null,
  au_geo_json: null,
  cup_geo_json: null,
  cup_boundary_geo_json: null,
}

export const mapDataReducer = (state, action) => {
  switch (action.type) {
    case "RESET": {
      return initialMapDataState
    }
    case "SET_DATA": {
      const { payload } = action
      const newState = { ...state }
      const state_layers = [
        "fac_geo_json",
        "au_geo_json",
        "cup_geo_json",
        "cup_boundary_geo_json",
        "province_geo_json",
        "district_geo_json",
        "sub_district_geo_json",
        "ward_geo_json",
      ]
      state_layers.forEach((layer) => {
        if (payload.hasOwnProperty(layer)) {
          newState[layer] = payload[layer]
        }
      })
      return newState
    }
    case "UPDATE_FACILITY": {
      const {
        payload: { facility },
      } = action
      const { fac_geo_json } = state
      return {
        ...state,
        fac_geo_json: {
          ...fac_geo_json,
          features: fac_geo_json.features.map((feature) =>
            feature.id === facility.id
              ? {
                  ...facility,
                }
              : feature,
          ),
        },
      }
    }
    case "UPDATE_CUP": {
      const {
        payload: { cup },
      } = action
      const { cup_geo_json } = state
      return {
        ...state,
        cup_geo_json: {
          ...cup_geo_json,
          features: cup_geo_json.features.map((feature) =>
            feature.id === cup.id
              ? {
                  ...cup,
                }
              : feature,
          ),
        },
      }
    }
    case "CREATE_FACILITY": {
      const {
        payload: { coords, properties },
      } = action
      let { fac_geo_json } = state
      const id = produce_id(fac_geo_json)
      const facility = create_point(id, coords, properties)
      fac_geo_json = {
        ...fac_geo_json,
        features: [...fac_geo_json.features, facility],
      }
      return { ...state, fac_geo_json }
    }
    case "CREATE_CUP": {
      const {
        payload: { coords, properties },
      } = action
      let { cup_geo_json } = state
      const id = produce_id(cup_geo_json)
      const cup = create_point(id, coords, properties)
      cup_geo_json = {
        ...cup_geo_json,
        features: [...cup_geo_json.features, cup],
      }
      return { ...state, cup_geo_json }
    }
    case "CREATE_CUPS_BATCH": {
      const id = produce_id(state.cup_geo_json)
      const new_features = action.payload.au_ids.map((au_id, index) => {
        const feature = feature_from_id(state.au_geo_json, au_id)
        const point = turf.centroid(feature)
        const pid = id + index
        const coords = {
          lat: point.geometry.coordinates[1],
          lng: point.geometry.coordinates[0],
        }
        return create_point(pid, coords, { name: "Hypothetical", fixed: false })
      })
      const fixed_cups = state.cup_geo_json.features.filter(
        (feature) => feature.properties.fixed,
      )
      return {
        ...state,
        cup_geo_json: {
          ...state.cup_geo_json,
          features: [...fixed_cups, ...new_features],
        },
      }
    }
    case "DELETE_FACILITY": {
      const {
        payload: { id },
      } = action
      let { fac_geo_json } = state
      fac_geo_json = {
        ...fac_geo_json,
        features: fac_geo_json.features.filter((feature) => feature.id !== id),
      }
      return { ...state, fac_geo_json }
    }
    case "DELETE_CUP": {
      const {
        payload: { id },
      } = action
      let { cup_geo_json, au_geo_json, cup_boundary_geo_json } = state
      cup_geo_json = {
        ...cup_geo_json,
        features: cup_geo_json.features.filter((feature) => feature.id !== id),
      }
      au_geo_json = {
        ...au_geo_json,
        features: au_geo_json.features.map((feature) => {
          if (feature.properties.cup_id !== id) return feature
          return {
            ...feature,
            properties: { ...feature.properties, cup_id: null },
          }
        }),
      }
      cup_boundary_geo_json = update_cup_boundaries(
        [id],
        au_geo_json,
        cup_boundary_geo_json,
      )
      return {
        ...state,
        au_geo_json,
        cup_geo_json,
        cup_boundary_geo_json,
      }
    }
    case "APPLY_ALLOCATION": {
      const {
        payload: { facilities_results, analysis_units_results },
      } = action
      let { fac_geo_json, au_geo_json } = state
      fac_geo_json = {
        ...fac_geo_json,
        features: fac_geo_json.features.map((feature) => {
          const results = facilities_results[feature.id]
          if (!results) return feature // leave unchanged
          const { utilised, unutilised, catchment_size } = results
          return {
            ...feature,
            properties: {
              ...feature.properties,
              utilised,
              unutilised,
              catchment_size,
            },
          }
        }),
      }
      au_geo_json = {
        ...au_geo_json,
        features: au_geo_json.features.map((feature) => {
          const au_id = feature.properties[feature_schema.au.id_property]
          const result = analysis_units_results[au_id]
          if (!result) return feature
          const updated_props = {
            ...result,
            catchment_group: categorise_allocation(result.catchment),
            allocated_to_group: categorise_allocation(result.allocated_to),
          }
          return {
            ...feature,
            properties: { ...feature.properties, ...updated_props },
          }
        }),
      }
      return {
        ...state,
        fac_geo_json,
        au_geo_json,
      }
    }
    case "UPDATE_CUP_ID": {
      const {
        payload: { client_id, server_id },
      } = action
      let { cup_geo_json, au_geo_json, cup_boundary_geo_json } = state
      cup_boundary_geo_json = {
        ...cup_boundary_geo_json,
        features: cup_boundary_geo_json.features.map((feature) =>
          feature.id === client_id
            ? {
                ...feature,
                id: server_id,
                properties: { ...feature.properties, server_id },
              }
            : feature,
        ),
      }
      cup_geo_json = {
        ...cup_geo_json,
        features: cup_geo_json.features.map((feature) =>
          feature.id === client_id
            ? {
                ...feature,
                id: server_id,
                properties: { ...feature.properties, server_id },
              }
            : feature,
        ),
      }
      au_geo_json = {
        ...au_geo_json,
        features: au_geo_json.features.map((feature) =>
          feature.properties.cup_id === client_id
            ? {
                ...feature,
                properties: { ...feature.properties, cup_id: server_id },
              }
            : feature,
        ),
      }
      return {
        ...state,
        cup_boundary_geo_json,
        cup_geo_json,
        au_geo_json,
      }
    }
    case "ASSIGN_WARD_TO_CUP": {
      const {
        payload: { ward_id, cup_id },
      } = action
      let { au_geo_json, cup_boundary_geo_json } = state
      const updated_au_geo_json = {
        ...au_geo_json,
        features: au_geo_json.features.map((feature) => {
          return ward_id === feature.properties.ward_id
            ? {
                ...feature,
                properties: {
                  ...feature.properties,
                  cup_id,
                },
              }
            : feature
        }),
      }
      const affected_cups = [
        ...new Set(
          [
            ...updated_au_geo_json.features
              .filter((feature) => ward_id === feature.properties.ward_id)
              .map(({ properties }) => properties.cup_id),
            ...au_geo_json.features
              .filter((feature) => ward_id === feature.properties.ward_id)
              .map(({ properties }) => properties.cup_id),
          ].filter((id) => id !== null && id !== undefined),
        ),
      ]
      cup_boundary_geo_json = update_cup_boundaries(
        affected_cups,
        updated_au_geo_json,
        cup_boundary_geo_json,
      )
      return {
        ...state,
        au_geo_json: updated_au_geo_json,
        cup_boundary_geo_json,
      }
    }
    case "UPDATE_CUP_LOCATION": {
      const {
        payload: { coords, id },
      } = action
      let { cup_geo_json } = state
      cup_geo_json = {
        ...cup_geo_json,
        features: cup_geo_json.features.map((feature) =>
          feature.id === id
            ? {
                ...feature,
                geometry: { ...feature.geometry, coordinates: coords },
              }
            : feature,
        ),
      }
      return {
        ...state,
        cup_geo_json,
      }
    }
    case "UPDATE_FACILITY_LOCATION": {
      const {
        payload: { coords, id },
      } = action
      let { fac_geo_json } = state
      fac_geo_json = {
        ...fac_geo_json,
        features: fac_geo_json.features.map((feature) =>
          feature.id === id
            ? {
                ...feature,
                geometry: { ...feature.geometry, coordinates: coords },
              }
            : feature,
        ),
      }
      return {
        ...state,
        fac_geo_json,
      }
    }
    default:
      return state
  }
}
